import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { assetGroupDefinition } from '../../../../entity-definitions/asset-group.definition';
import { assetDefinition } from '../../../../entity-definitions/asset.definition';
import { AssetTypeStore } from '../../../../stores/asset-type.store';
import { AssetGroup } from '../../../../types/asset-group.interface';
import { Asset } from '../../../../types/asset.interface';
import { EntityDefinition } from '../../../../types/entity-definition.interface';
import { FormulaParameters } from '../../../../types/formula.interface';
import { AssetFormComponent } from '../../../forms/asset-form.component';
import { AssetGroupFormComponent } from '../../../forms/asset-group-form.component';
import { CesButtonsModule } from '../../../ui/ces-buttons/ces-buttons.module';
import { CesIconComponent } from '../../../ui/ces-icon/ces-icon.component';
import { EditorComponent } from '../../../ui/inputs/editor/editor.component';
import { EditorVariable } from '../../../ui/inputs/editor/editor.types';
import { ParametersComponent } from '../../../ui/inputs/parameters-input/parameters.component';
import { SidebarSection } from '../../../ui/sidebar/generic-sidebar/generic-sidebar.component';
import { DemoSidebarComponent, MockIconRoute } from './demo-sidebar.component';
import { IconRoute } from '../../../../app.routes';

import { CesTooltipAutoDismissDirective } from '../../../../directives/ces-tooltip-auto-dismiss.directive';
@Component({
  selector: 'app-component-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    ButtonModule,
    CardModule,
    CesIconComponent,
    DemoSidebarComponent,
    JsonPipe,
    TagModule,
    AssetFormComponent,
    AssetGroupFormComponent,
    ParametersComponent,
    EditorComponent,
    CesButtonsModule,
    Tooltip,
  ],
  templateUrl: './component-list.component.html',
  styleUrl: './component-list.component.scss',
})
export class ComponentListComponent implements OnInit {
  parameterValue: FormulaParameters = [];
  expressionValue: string = 'VALUE(TPS) * 10 / VOL + ABS(1)';
  expressionVariables: EditorVariable[] = [
    {
      description: 'Temps',
      name: 'TPS',
      value: 'TPS',
    },
    {
      description: 'Volume',
      name: 'VOL',
      value: 'VOL',
    },
  ];

  readonly assetGroupConfig: EntityDefinition<AssetGroup> = assetGroupDefinition;
  readonly assetConfig: EntityDefinition<Asset> = assetDefinition;
  readonly assetTypeStore = inject(AssetTypeStore);

  ngOnInit(): void {
    void this.assetTypeStore.loadAll();
  }

  readonly isCollapsed = signal(false);
  readonly activeMockRouteIndex = signal(0);

  sidebarSections: SidebarSection[] = [
    {
      path: 'general',
      title: 'Général',
      collapsed: false,
      routes: [
        { path: 'dashboard', title: 'Tableau de bord', icon: 'dashboard' },
        { path: 'settings', title: 'Paramètres', icon: 'settings' },
        { path: 'profile', title: 'Profil', icon: 'person' },
      ] as MockIconRoute[],
    },
    {
      path: 'components',
      title: 'Composants',
      collapsed: true,
      routes: [
        { path: 'buttons', title: 'Boutons', icon: 'smart_button' },
        { path: 'forms', title: 'Formulaires', icon: 'edit_note' },
        { path: 'tables', title: 'Tableaux', icon: 'table_chart' },
        {
          path: 'modals',
          title: 'Fenêtres modales',
          icon: 'web_asset',
          disabled: true,
        },
      ] as MockIconRoute[],
    },
    {
      path: 'data',
      title: 'Données',
      collapsed: true,
      routes: [
        { path: 'users', title: 'Utilisateurs', icon: 'group' },
        { path: 'products', title: 'Produits', icon: 'inventory_2' },
        {
          path: 'analytics',
          title: 'Analytiques',
          icon: 'analytics',
          disabled: true,
        },
      ] as MockIconRoute[],
    },
    {
      path: 'admin',
      title: 'Administration',
      collapsed: true,
      routes: [
        {
          path: 'users-management',
          title: 'Gestion utilisateurs',
          icon: 'admin_panel_settings',
        },
        {
          path: 'roles',
          title: "Profils d'accès",
          icon: 'verified_user',
          disabled: true,
        },
        { path: 'logs', title: 'Journaux système', icon: 'receipt_long' },
      ] as MockIconRoute[],
    },
  ];

  userMenuItems: MenuItem[] = [
    {
      label: 'Mon profil',
      icon: 'person',
      command: () => console.log('Profile clicked'),
    },
    {
      label: 'Mes paramètres',
      icon: 'settings',
      command: () => console.log('Settings clicked'),
    },
    { separator: true },
    {
      label: 'Déconnexion',
      icon: 'logout',
      command: () => console.log('Logout clicked'),
    },
  ];

  settingsMenuItems: MenuItem[] = [
    {
      label: 'Configuration générale',
      icon: 'tune',
      command: () => console.log('General config clicked'),
    },
    {
      label: 'Utilisateurs',
      icon: 'group',
      command: () => console.log('Users clicked'),
    },
    {
      label: 'Sécurité',
      icon: 'security',
      command: () => console.log('Security clicked'),
    },
  ];

  console = console;
  log = this.console.log;
  items: MenuItem[] = [
    {
      label: 'Premier',
      items: [
        {
          label: 'Sous-niveau 1',
          command: () => this.log('label 1'),
        },
        {
          label: 'Sous-niveau 2',
          command: () => this.log('label 2'),
        },
      ],
    },
    {
      label: 'Second',
      items: [
        {
          label: 'Sous-niveau 3',
          command: () => this.log('label 3'),
        },
        {
          label: 'Sous-niveau 4',
          command: () => this.log('label 4'),
        },
      ],
    },
    { label: 'Troisième', url: '' },
  ];

  toggleSidebar(): void {
    this.isCollapsed.update(value => !value);
    console.log('Toggle sidebar:', this.isCollapsed());
  }

  toggleSectionCollapse(sectionIndex: number): void {
    const section = this.sidebarSections[sectionIndex];
    if (!section) return;

    const updated: SidebarSection = { ...section, collapsed: !section.collapsed };
    this.sidebarSections[sectionIndex] = updated;
    console.log(`Section ${sectionIndex} collapsed:`, updated.collapsed);
  }

  isRouteDisabled(route: IconRoute): boolean {
    return route.disabled === true;
  }

  getActiveMockRoute(): MockIconRoute {
    const allRoutes = this.sidebarSections.flatMap(section => section.routes as MockIconRoute[]);
    const currentIndex = this.activeMockRouteIndex();
    const route = allRoutes[currentIndex] ?? allRoutes[0];
    if (!route) {
      throw new Error('component-list: sidebarSections must contain at least one route');
    }
    return route;
  }

  toggleActiveMockRoute(): void {
    const totalRoutes = this.sidebarSections.reduce((total, section) => total + section.routes.length, 0);
    this.activeMockRouteIndex.update(index => (index + 1) % totalRoutes);
  }

  goHome(): void {
    console.log("Navigation vers la page d'accueil");
  }

  handleDeleteConfirm(type: string): void {
    console.log(`Suppression confirmée pour: ${type}`);
  }

  handleDeleteReject(type: string): void {
    console.log(`Suppression rejetée pour: ${type}`);
  }
}
